import { Link } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';

const AboutPage = () => {
  const values = [
    {
      icon: 'ri-award-line',
      title: 'Quality Workmanship',
      description: 'We take pride in delivering exceptional results on every project, using quality materials and proven techniques.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Clear Communication',
      description: 'We believe clear communication is essential. We keep you informed throughout the process and discuss any changes or requirements as they arise.',
    },
    {
      icon: 'ri-time-line',
      title: 'Reliable & Professional Service',
      description: 'We provide a reliable and professional service, managing works in an organised manner and delivering projects to an agreed standard.',
    },
  ];

  const certifications = [
    { name: 'Checkatrade Member', icon: 'ri-shield-check-line' },
    { name: 'Fully Insured', icon: 'ri-shield-star-line' },
    { name: 'Gas Safe Registered', icon: 'ri-fire-line' },
    { name: 'NICEIC Approved', icon: 'ri-flashlight-line' },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Company Story Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-[#F5F3EF]">
        <div className="max-w-[700px] mx-auto">
          <div className="inline-block border border-gray-400 rounded-full px-4 py-2 mb-6">
            <span className="text-xs sm:text-sm text-gray-700 break-words">About Minuu Property Services</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-8 leading-tight font-serif break-words">
            Professional Building Services You Can Trust
          </h1>
          <div className="space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
            <p className="break-words">
              Minuu Property Services is a trusted building and property maintenance company serving homeowners, landlords and managing agents across London. We specialise in delivering high-quality building work, refurbishments and ongoing property maintenance with professionalism and care.
            </p>
            <p className="break-words">
              Our experienced team handles projects of all sizes, from small repairs and routine maintenance to complete kitchen and bathroom refurbishments, extensions and full property renovations. Whatever your property needs, we have the skills and expertise to deliver exceptional results.
            </p>
            <p className="break-words">
              As a Checkatrade-registered company, we are committed to maintaining high standards of workmanship and customer service. Our reputation is built on reliability, transparency and quality - values that guide everything we do.
            </p>
            <p className="break-words">
              We are also part of the Minuu Group, a London-based construction and property services business with over 20 years' experience delivering residential projects.
            </p>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <img
                src="https://public.readdy.ai/ai/img_res/b2caaae863f54923a0b86f1202f75ada.jpg"
                alt="Our team at work"
                className="w-full h-[500px] object-cover rounded-2xl shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <img
                src="https://readdy.ai/api/search-image?query=completed%20modern%20home%20renovation%20project%20showing%20beautiful%20finished%20interior%20with%20quality%20craftsmanship%20and%20professional%20building%20work%20in%20bright%20contemporary%20setting&width=400&height=240&seq=completed-project-1&orientation=landscape"
                alt="Completed project"
                className="w-full h-[240px] object-cover rounded-2xl shadow-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=professional%20building%20tools%20and%20equipment%20neatly%20organized%20showing%20quality%20workmanship%20standards%20and%20professional%20approach%20to%20construction%20work%20in%20clean%20setting&width=400&height=240&seq=equipment-image&orientation=landscape"
                alt="Professional equipment"
                className="w-full h-[240px] object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 px-6 lg:px-12 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-black text-center mb-12 break-words">
            Our Commitments
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#F5F3EF] rounded-full mb-6">
                  <i className={`${value.icon} text-4xl text-black`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-4 break-words">{value.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team/Credentials Section */}
      <section className="py-20 px-6 lg:px-12 bg-[#F5F3EF]">
        <div className="max-w-[1000px] mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 break-words">
            Qualified &amp; Insured
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-12 max-w-2xl mx-auto leading-relaxed break-words">
            We hold all necessary qualifications and insurance to carry out building, plumbing and electrical work safely and to the highest standards. Your property and peace of mind are protected.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full mb-4 shadow-md">
                  <i className={`${cert.icon} text-4xl text-black`}></i>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-black text-center break-words">{cert.name}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm">
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed break-words">
              <strong className="text-black">Comprehensive Insurance:</strong> We carry full public liability insurance and employer's liability insurance, giving you complete protection and peace of mind throughout your project.
            </p>
          </div>
        </div>
      </section>

      {/* Minuu Group Connection */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-black mb-6 break-words">
            Part of the Minuu Group
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-8 leading-relaxed break-words">
            Minuu Property Services is part of the Minuu Group, a London-based construction and property services business with over 20 years' experience delivering residential projects.
          </p>
          <p className="text-base sm:text-lg text-gray-700 mb-8 leading-relaxed break-words">
            Being part of an established group allows us to draw on wider construction knowledge and experience, while remaining focused on providing reliable, high-quality building and property services.
          </p>
          <div className="inline-block bg-[#F5F3EF] rounded-2xl px-8 py-6">
            <img
              src="https://static.readdy.ai/image/6b8c7e9e23efe7296b6664942db70801/3caeb8f973bb8474b481b58f740cea1e.png"
              alt="Minuu Group"
              className="h-20 mx-auto"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-[#2B2B2B]">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 break-words">
            Work With a Team You Can Trust
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 break-words">
            Get in touch to discuss your project
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#F5F3EF] text-black px-8 sm:px-10 py-5 rounded-full text-base sm:text-lg font-semibold hover:bg-white transition-all whitespace-nowrap cursor-pointer"
          >
            Request Your Free Quote
            <i className="ri-arrow-right-line"></i>
          </Link>
          <div className="mt-8 flex items-center justify-center gap-2 text-white">
            <i className="ri-phone-fill text-xl sm:text-2xl"></i>
            <a href="tel:07773467507" className="text-xl sm:text-2xl font-bold hover:text-gray-300 transition-colors cursor-pointer break-all">
              07773 467507
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
